import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Branded split-screen shell shared by the login / forgot-password /
 * reset-password screens: a thematic hero panel (wordmark + Activateur value
 * props, copy from cqdd.qc.ca/activateur) on the left, the form on the right.
 * The hero is hidden below `md`, leaving a clean centered form on mobile.
 */
export function AuthScreen({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4 md:p-8">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-2xl border bg-card shadow-xl md:min-h-[560px] md:grid-cols-2">
        {/* Hero */}
        <div
          className="relative hidden flex-col overflow-hidden p-10 text-white md:flex"
          style={{ backgroundColor: "hsl(210 30% 13%)" }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(120% 80% at 15% 10%, hsl(275 40% 40% / 0.55), transparent 60%), radial-gradient(120% 80% at 90% 90%, hsl(142 55% 34% / 0.55), transparent 55%), radial-gradient(100% 70% at 90% 15%, hsl(189 70% 40% / 0.4), transparent 55%)",
            }}
            aria-hidden
          />
          <div className="relative flex flex-1 flex-col">
            <div className="mb-7 flex gap-1" aria-hidden>
              <span className="h-1 w-8 rounded-full bg-theme-governance" />
              <span className="h-1 w-8 rounded-full bg-theme-economic" />
              <span className="h-1 w-8 rounded-full bg-theme-social" />
              <span className="h-1 w-8 rounded-full bg-theme-environment" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight">
              activ<span style={{ color: "hsl(0 80% 66%)" }}>a</span>teur
            </div>
            <p className="mt-auto max-w-[20ch] text-xl font-bold leading-tight">
              {t("auth.hero.tagline")}
            </p>
            <p className="mt-3 max-w-[36ch] text-sm text-white/75">
              {t("auth.hero.subtitle")}
            </p>
            <ul className="mt-6 space-y-2.5">
              {["diagnostic", "plan", "share"].map((key) => (
                <li key={key} className="flex items-center gap-2.5 text-sm text-white/90">
                  <Check
                    className="size-[18px] shrink-0"
                    style={{ color: "hsl(142 55% 70%)" }}
                    aria-hidden
                  />
                  {t(`auth.hero.props.${key}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Form column */}
        <div className="flex flex-col p-8 sm:p-10">
          <div className="flex justify-end">
            <LanguageSwitcher />
          </div>
          <div className="my-auto w-full max-w-sm self-center py-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
